
import { Link, useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { Button } from "@/components/ui/button";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ChevronLeft, Loader2 } from "lucide-react";
import DocumentForm from "@/components/documents/DocumentForm";
import { documentApi } from "@/services/api";
import { useToast } from "@/components/ui/use-toast";

const DocumentDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Check if id is present and convert to number
  const documentId = id ? parseInt(id) : undefined;
  
  // Fetch document details
  const { 
    data: documentData, 
    isLoading, 
    isError, 
    error,
    refetch,
  } = useQuery({
    queryKey: ['document', documentId],
    queryFn: () => documentId ? documentApi.getById(documentId) : Promise.reject('No document ID provided'),
    enabled: !!documentId,
    meta: {
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "Error loading document",
          description: error.message || "Failed to fetch document details",
        });
      }
    }
  });
  
  // Function to handle document update success
  const handleUpdateSuccess = async () => {
    // Refetch the document data to get the latest status
    await refetch();
    
    toast({
      title: "Document updated",
      description: "The document has been updated successfully.",
    });
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    console.log("Form submission event:", e);
    // Use window.document to avoid conflict with the imported 'document' type
    const formBuilder = window.document.querySelector('[data-form-builder="true"]');
    if (formBuilder && formBuilder.contains(e.target as Node)) {
      console.log("Preventing form submission from form builder");
      e.preventDefault();
      e.stopPropagation();
    }
  };

  if (isLoading) {
    return (
      <BackofficeLayout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
        </div>
      </BackofficeLayout>
    );
  }

  if (isError || !documentData) {
    return (
      <BackofficeLayout>
        <div className="space-y-6">
          <Button asChild variant="outline" size="sm">
            <Link to="/backoffice/documents">
              <ChevronLeft className="mr-1 h-4 w-4" />
              Back to Documents
            </Link>
          </Button>
          
          <Alert variant="destructive">
            <AlertDescription>
              {(error as any)?.message || "Failed to load document. The document may have been deleted or you don't have permission to view it."}
            </AlertDescription>
          </Alert>
        </div>
      </BackofficeLayout>
    );
  }

  return (
    <BackofficeLayout>
      <div className="space-y-6" onSubmit={handleFormSubmit}>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="space-y-1">
            <Button asChild variant="outline" size="sm">
              <Link to="/backoffice/documents">
                <ChevronLeft className="mr-1 h-4 w-4" />
                Back to Documents
              </Link>
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">
              {documentData.name}
            </h1>
            <p className="text-gray-500">
              View and manage document details
            </p>
          </div>
          
          <Button 
            type="submit"
            form="document-detail-form"
          >
            Update Document
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Document Details</CardTitle>
            <CardDescription>
              Viewing document information
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DocumentForm 
              documentProp={documentData} 
              onSuccess={handleUpdateSuccess} 
              readOnly={false}
              formId="document-detail-form"
              preventFormSubmission={true}
            />
          </CardContent>
        </Card>
      </div>
    </BackofficeLayout>
  );
};

export default DocumentDetail;
